import { Badge } from '@/components/ui/badge';
import { LEVEL_LABEL, levelForJobRole } from '@/lib/roles';
import type { AccessLevel, ContactSource, JobRole, JobStatus, MessageStatus, RecipientStatus, TenantRole, UserStatus } from '@/lib/types';

export function RecipientStatusBadge({ status }: { status: RecipientStatus }) {
  const map: Record<RecipientStatus, { label: string; variant: 'success' | 'danger' | 'muted' | 'warning' | 'secondary' }> = {
    queued: { label: 'Queued', variant: 'muted' },
    sending: { label: 'Sending', variant: 'muted' },
    sent: { label: 'Sent', variant: 'secondary' },
    delivered: { label: 'Delivered', variant: 'success' },
    undelivered: { label: 'Undelivered', variant: 'danger' },
    failed: { label: 'Failed', variant: 'danger' },
    skipped_opted_out: { label: 'Opted out', variant: 'warning' },
    skipped_invalid: { label: 'Invalid number', variant: 'warning' },
  };
  const m = map[status];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

export function MessageStatusBadge({ status }: { status: MessageStatus }) {
  const map: Record<MessageStatus, { label: string; variant: 'success' | 'danger' | 'muted' | 'secondary' | 'outline' | 'warning' }> = {
    draft: { label: 'Draft', variant: 'outline' },
    scheduled: { label: 'Scheduled', variant: 'warning' },
    queued: { label: 'Queued', variant: 'muted' },
    sending: { label: 'Sending', variant: 'secondary' },
    completed: { label: 'Completed', variant: 'success' },
    failed: { label: 'Failed', variant: 'danger' },
    cancelled: { label: 'Cancelled', variant: 'muted' },
  };
  const m = map[status];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

const LEVEL_VARIANT: Record<AccessLevel, 'default' | 'secondary' | 'outline'> = { super_user: 'default', pm_admin: 'secondary', pm_user: 'outline' };

/** Super User · PM Admin · PM User */
export function AccessLevelBadge({ level, className }: { level: AccessLevel; className?: string }) {
  return (
    <Badge variant={LEVEL_VARIANT[level]} className={className}>
      {LEVEL_LABEL[level]}
    </Badge>
  );
}

/** A job membership role shown as its PM level. */
export function RoleBadge({ role }: { role: JobRole | null | undefined }) {
  if (!role) return <Badge variant="muted">—</Badge>;
  return <AccessLevelBadge level={levelForJobRole(role)} />;
}

/** Organization role: Super User, or Project Manager (level per job). */
export function TenantRoleBadge({ role }: { role: TenantRole }) {
  return <Badge variant={role === 'org_admin' ? 'default' : 'outline'}>{role === 'org_admin' ? 'Super User' : 'Project Manager'}</Badge>;
}

export function UserStatusBadge({ status }: { status: UserStatus }) {
  return <Badge variant={status === 'active' ? 'success' : 'muted'}>{status === 'active' ? 'Active' : 'Disabled'}</Badge>;
}

export function JobStatusBadge({ status }: { status: JobStatus }) {
  return <Badge variant={status === 'active' ? 'success' : 'muted'}>{status === 'active' ? 'Active' : 'Archived'}</Badge>;
}

export function ContactSourceBadge({ source }: { source: ContactSource }) {
  if (source === 'self_service') return <Badge variant="secondary">Joined by text</Badge>;
  if (source === 'web_form') return <Badge variant="secondary">Web sign-up</Badge>;
  if (source === 'import') return <Badge variant="outline">Imported</Badge>;
  if (source === 'api') return <Badge variant="outline">API</Badge>;
  return <Badge variant="outline">Added manually</Badge>;
}
